import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

import type {
  RouteStopIntent,
  RouteStopRole,
  RouteStopType,
} from './routeStopKind';

/**
 * Route Plans (CR-2) — reusable route templates served by the `route` module
 * under `/v1/route-plans`. A plan is an ordered chain of stops (snapshot + an
 * optional lane pointer) with SERVER-computed legs; the UJP wizard and the 4W
 * stops step pick a plan and the consumer snapshots it, so editing or
 * deactivating a plan never rewrites a past UJP or shipment.
 *
 * Envelope `{ status: 'Success', data }`; errors `{ status: 'Failed', error }`
 * with 400 (validation) / 409 (duplicate name). Km / distances travel as
 * decimal STRINGS on the wire — read them through `toNum`, never do arithmetic
 * on the raw field.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Vocabulary
// ─────────────────────────────────────────────────────────────────────────────

/**
 * CR-6 (§22): a stop is described by the WORKFLOW vocabulary — `type` +
 * `intent` + `pool` (see `routeStopKind.ts`). The legacy planner `role` is
 * DERIVED by the server (PICKUP∧pool → POSITIONING, DROP_OFF∧pool → RETURN)
 * and still present on every response; requests may send either set. Read a
 * stop through `kindOf()` — never branch on `role` in new code.
 */
export type RoutePlanStopType = RouteStopType;
export type RoutePlanStopIntent = RouteStopIntent;

/** Legacy planner role (derived since CR-6; kept for a release, CR6-D5). */
export type RoutePlanStopRole = RouteStopRole;

/**
 * How the server measured a leg (CR2-D3): a known `addresses` lane
 * (coordinate-matched), Mapbox Directions, or a haversine fallback shown as an
 * "estimasi" to ops.
 */
export type RoutePlanLegSource = 'LANE' | 'DIRECTIONS' | 'HAVERSINE';

/** Which side of the owning lane a stop points at, when it points at one. */
export type RoutePlanEndpoint = 'origin' | 'destination';

/** Decimal string on the wire (`"62.4"`); a number is equally valid on read. */
export type NumberLike = string | number;

// ─────────────────────────────────────────────────────────────────────────────
// Shapes
// ─────────────────────────────────────────────────────────────────────────────

export interface RoutePlanStop {
  seq: number;
  /** Workflow stop type (CR6-D1). Absent only on a pre-CR-6 snapshot → derive from `role`. */
  type?: RoutePlanStopType | null;
  /** PICKUP is always GENERIC; a pool DROP_OFF defaults to GENERIC, a billable one to UNLOAD. */
  intent?: RoutePlanStopIntent | null;
  /** The empty positioning / return leg — not charged unless the client is (CR6-D3). */
  pool?: boolean | null;
  /**
   * CR-8 (D1): billing override for a pool stop. `null` = follow the client
   * rule; `true`/`false` wins over it (D2). Echoed on every response stop
   * (null when unset); meaningless on a non-pool stop.
   */
  charged?: boolean | null;
  /** DERIVED legacy role — always echoed by the server (CR6-D2). */
  role: RoutePlanStopRole;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  /** Lane pointer (CR2-D4); `null` for a manual stop until the write-back lands. */
  addressId: string | null;
  endpoint: RoutePlanEndpoint | null;
  /**
   * CR-5 per-stop workflow pin: an ACTIVE stop workflow of the same client
   * whose `stopType` matches the stop's `type` (and whose intent fits, CR6-D1).
   * `null`/absent = follow the client default.
   */
  workflowId?: string | null;
  /** Display name, refreshed from the workflow master by the server on save. */
  workflowName?: string | null;
}

export interface RoutePlanLeg {
  seq: number;
  fromSeq: number;
  toSeq: number;
  /** Decimal string (`"62.4"`). */
  km: NumberLike;
  source: RoutePlanLegSource;
  /** True once ops typed the km by hand — the source is kept for audit. */
  edited: boolean;
}

/** Server limit on cost items per plan. */
export const ROUTE_PLAN_COST_ITEMS_MAX = 20;

/**
 * One usual cost of the route: a name and an amount (money string, "150000" /
 * "150000.50"). Copied 1:1 into the UJP cost rows on pick (PLAN-UJP-DYNAMIC-COSTS).
 */
export interface RoutePlanCostItem {
  label: string;
  amount: string;
}

export interface RoutePlan {
  id: string;
  clientId: number;
  name: string;
  stops: RoutePlanStop[];
  legs: RoutePlanLeg[];
  totalKm: NumberLike;
  /**
   * Σ legs whose two endpoints are both billed (CR2-D7 / CR6-D3 / CR8-D4).
   * The planner API has no client rule, so it computes this with a `false`
   * default: a pool stop counts only when its own `charged` is `true`. The
   * UJP wizard is authoritative for the client-aware number.
   */
  kmCharged: NumberLike;
  active: boolean;
  /** Usual costs that pre-fill the UJP Biaya step. Absent from a backend that
   *  predates cost items — read through `planCostItems()`. */
  costItems?: RoutePlanCostItem[];
  /** Free-text note shown under the route picker in the UJP wizard. */
  notes?: string | null;
  /** Fixed BBM amount: picking the plan turns the UJP's "Override BBM" on with
   *  this nominal. Absent from a backend that predates it; null = none. */
  bbmFixOverride?: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The plan's cost items; `[]` when none or when the backend predates them. */
export const planCostItems = (plan: Pick<RoutePlan, 'costItems'>): RoutePlanCostItem[] =>
  plan.costItems ?? [];

/**
 * Snapshot drift (CR2-D4): the stored stop no longer matches its current lane
 * — `field` names the stop column (`address`, `latitude`, …) and `current` the
 * lane's value. CR-5b (TODO-35) adds `field: 'workflow'` for a pinned workflow
 * whose master changed: `current` is {@link WORKFLOW_DRIFT_DELETED},
 * {@link WORKFLOW_DRIFT_INACTIVE}, or the master's NEW name after a rename.
 * Workflow drift is advisory — it never feeds "Refresh dari lane".
 */
export interface RoutePlanDrift {
  seq: number;
  field: string;
  current: string | number;
}

export const WORKFLOW_DRIFT_FIELD = 'workflow';
export const WORKFLOW_DRIFT_DELETED = 'workflow dihapus';
export const WORKFLOW_DRIFT_INACTIVE = 'workflow nonaktif';

export const isWorkflowDrift = (d: RoutePlanDrift): boolean =>
  d.field === WORKFLOW_DRIFT_FIELD;

export type WorkflowDriftKind = 'deleted' | 'inactive' | 'renamed';

/** Classify a `workflow` drift entry's `current` sentinel. */
export const workflowDriftKind = (d: RoutePlanDrift): WorkflowDriftKind => {
  if (d.current === WORKFLOW_DRIFT_DELETED) return 'deleted';
  if (d.current === WORKFLOW_DRIFT_INACTIVE) return 'inactive';
  return 'renamed';
};

export interface RoutePlanDetail extends RoutePlan {
  drift?: RoutePlanDrift[];
}

/** Best-effort DRAFT-lane write-back result (CR2-D5/D8). */
export interface RoutePlanLaneWriteBack {
  written: number;
  failed: Array<{ seq: number; reason: string }>;
}

// ── Requests ─────────────────────────────────────────────────────────────────

export interface RoutePlanListParams {
  clientId: number;
  search?: string;
  /** Omit for all states; `true` for only-active (the picker default). */
  active?: boolean;
}

/**
 * A stop as sent on create/update. New code sends `type` + `intent` + `pool`
 * (CR6-D5); `role` is optional — the server accepts either set (one of them
 * required) and derives the other. The planner still sends the derived `role`
 * alongside for a release so old readers keep working.
 */
export interface RoutePlanStopInput {
  seq: number;
  type?: RoutePlanStopType | null;
  intent?: RoutePlanStopIntent | null;
  pool?: boolean | null;
  /** CR-8: send on a pool stop (`null` = follow the client rule); omit otherwise. */
  charged?: boolean | null;
  role?: RoutePlanStopRole | null;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  addressId?: string | null;
  endpoint?: RoutePlanEndpoint | null;
  /** CR-5 workflow pin — rejected 400 (`WORKFLOW_*`) when it does not fit. */
  workflowId?: string | null;
  workflowName?: string | null;
}

/**
 * The `error` codes a bad workflow pin is rejected with (400) on create/update,
 * and the `reason` codes a plan-sourced pin falls back to the default with at
 * shipment creation (`workflowFallback[]`, CR5-D3).
 */
export type WorkflowPinProblem =
  | 'WORKFLOW_NOT_FOUND'
  | 'WORKFLOW_CLIENT_MISMATCH'
  | 'WORKFLOW_STOP_TYPE_MISMATCH'
  | 'WORKFLOW_NOT_ACTIVE';

export const WORKFLOW_PIN_PROBLEM_LABELS: Record<WorkflowPinProblem, string> = {
  WORKFLOW_NOT_FOUND: 'workflow tidak ditemukan',
  WORKFLOW_NOT_ACTIVE: 'workflow nonaktif',
  WORKFLOW_STOP_TYPE_MISMATCH: 'tipe stop tidak cocok',
  WORKFLOW_CLIENT_MISMATCH: 'workflow milik client lain',
};

/** Human label for a fallback/rejection reason; unknown codes pass through. */
export const workflowPinProblemLabel = (reason: string): string =>
  WORKFLOW_PIN_PROBLEM_LABELS[reason as WorkflowPinProblem] ?? reason;

/** One plan-pinned workflow the server replaced with the slot default (CR5-D3). */
export interface WorkflowFallback {
  /** 1-based position among the stops sent to the shipment. */
  seq: number;
  reason: string;
}

export interface RoutePlanLegInput {
  seq: number;
  fromSeq: number;
  toSeq: number;
  km: NumberLike;
  source: RoutePlanLegSource;
  edited: boolean;
}

export interface CreateRoutePlanPayload {
  clientId: number;
  name: string;
  stops: RoutePlanStopInput[];
  legs: RoutePlanLegInput[];
  costItems?: RoutePlanCostItem[];
  notes?: string | null;
  /** `null` or omitted = none. */
  bbmFixOverride?: string | null;
}

export interface UpdateRoutePlanPayload {
  name?: string;
  active?: boolean;
  stops?: RoutePlanStopInput[];
  legs?: RoutePlanLegInput[];
  /** Replaces the whole list; `[]` clears it; omit to leave it unchanged. */
  costItems?: RoutePlanCostItem[];
  /** `null` clears; omit to leave it unchanged. */
  notes?: string | null;
  /** `null` removes the override; omit to leave it unchanged. */
  bbmFixOverride?: string | null;
}

export interface RoutePlanMutationResult {
  plan: RoutePlan;
  /** Absent on a pure rename / deactivate that never touched the lanes. */
  laneWriteBack?: RoutePlanLaneWriteBack;
}

/** One stop for the legs computation — a lane pointer OR bare coordinates. */
export interface ComputeLegsStop {
  seq: number;
  /** Feeds the server's lane lookup (name-key match); optional for a half-typed stop. */
  name?: string;
  addressId?: string | null;
  endpoint?: RoutePlanEndpoint | null;
  latitude: number;
  longitude: number;
}

export interface ComputeLegsPayload {
  clientId: number;
  stops: ComputeLegsStop[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Errors
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Carries the HTTP status so a caller can branch on 409 (duplicate name) or
 * 400 without string-matching, and `aborted` so a superseded legs request the
 * hook itself cancelled never surfaces as a failure.
 */
export class RoutePlanApiError extends Error {
  status?: number;
  aborted: boolean;

  constructor(message: string, status?: number, aborted = false) {
    super(message);
    this.name = 'RoutePlanApiError';
    this.status = status;
    this.aborted = aborted;
  }
}

const isAbort = (error: any): boolean =>
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.name === 'AbortError';

const toError = (error: any): RoutePlanApiError =>
  new RoutePlanApiError(
    isAbort(error) ? 'Perhitungan jarak dibatalkan' : getBackendErrorMessage(error),
    error?.response?.status,
    isAbort(error)
  );

// ─────────────────────────────────────────────────────────────────────────────
// Envelope
// ─────────────────────────────────────────────────────────────────────────────

type Envelope<T> = { status: string | number; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────

export const routePlansAPI = {
  /** Active plans for a client (CR2-D2/D9). `search` ILIKEs the name. */
  list: async (params: RoutePlanListParams): Promise<RoutePlan[]> => {
    try {
      const response = await Axios.get('/v1/route-plans', {
        params: {
          clientId: params.clientId,
          ...(params.search ? { search: params.search } : {}),
          ...(params.active != null ? { active: params.active } : {}),
        },
      });
      const body = unwrap<{ data: RoutePlan[] } | RoutePlan[]>(response?.data);
      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      throw toError(error);
    }
  },

  /** One plan with `drift` when a snapshot no longer matches its lane. */
  get: async (id: string): Promise<RoutePlanDetail> => {
    try {
      const response = await Axios.get(`/v1/route-plans/${id}`);
      const body = unwrap<
        { plan: RoutePlan; drift?: RoutePlanDrift[] } | RoutePlanDetail
      >(response?.data);
      // BE returns { plan, drift }; RoutePlanDetail is the flat plan + drift.
      return 'plan' in body
        ? { ...(body.plan as RoutePlan), drift: body.drift }
        : (body as RoutePlanDetail);
    } catch (error) {
      throw toError(error);
    }
  },

  /** 201 `{ plan, laneWriteBack }`; 409 on a duplicate name (case-insensitive). */
  create: async (
    payload: CreateRoutePlanPayload
  ): Promise<RoutePlanMutationResult> => {
    try {
      const response = await Axios.post('/v1/route-plans', payload);
      return unwrap<RoutePlanMutationResult>(response?.data);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Rename / deactivate (`active:false` hides, no 409 — CR2-D16) / re-save stops. */
  update: async (
    id: string,
    payload: UpdateRoutePlanPayload
  ): Promise<RoutePlanMutationResult> => {
    try {
      const response = await Axios.patch(`/v1/route-plans/${id}`, payload);
      return unwrap<RoutePlanMutationResult>(response?.data);
    } catch (error) {
      throw toError(error);
    }
  },

  /**
   * Server-computed legs (CR2-D3): lane-on-coordinate-match → Directions →
   * haversine. Pass `signal` so a superseded call aborts.
   */
  computeLegs: async (
    payload: ComputeLegsPayload,
    signal?: AbortSignal
  ): Promise<RoutePlanLeg[]> => {
    try {
      const response = await Axios.post('/v1/route-plans/legs', payload, {
        signal,
      });
      const body = unwrap<{ legs: RoutePlanLeg[] } | RoutePlanLeg[]>(
        response?.data
      );
      return Array.isArray(body) ? body : (body?.legs ?? []);
    } catch (error) {
      throw toError(error);
    }
  },
};
